/**
 * Keeps the sticky WhatsApp button clear of the sticky add-to-cart bar.
 *
 * The bar toggles `data-stuck="true"` on itself once the shopper scrolls past the buy
 * buttons (see sticky-add-to-cart.js). While it's stuck to the bottom of the screen,
 * raise this button above it so the two don't overlap. The lift itself is applied by
 * CSS at the mobile breakpoint only, since the bar is full-width there.
 */
class WhatsappStickyButtonComponent extends HTMLElement {
  connectedCallback() {
    this.#watchStickyAddToCartBar();
  }

  disconnectedCallback() {
    this.stickyBarObserver?.disconnect();
  }

  #watchStickyAddToCartBar() {
    const bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!bar) return;

    const syncRaisedState = () => {
      const isStuck = bar.getAttribute('data-stuck') === 'true';
      if (isStuck) {
        this.style.setProperty('--whatsapp-sticky-button-raise', `${bar.offsetHeight + 12}px`);
      }
      this.dataset.raised = String(isStuck);
    };

    syncRaisedState();
    this.stickyBarObserver = new MutationObserver(syncRaisedState);
    this.stickyBarObserver.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
  }
}

if (!customElements.get('whatsapp-sticky-button-component')) {
  customElements.define('whatsapp-sticky-button-component', WhatsappStickyButtonComponent);
}

/**
 * Reports clicks on the WhatsApp header action and this sticky button to Google
 * Analytics as a `whatsapp_click` event, with `click_location` set to `header` or
 * `sticky_button`.
 *
 * Lives here rather than in its own file to avoid an extra request — this script is
 * already loaded whenever WhatsApp is enabled, which is also when the header action
 * renders. A single delegated listener keeps re-rendered header links tracked.
 *
 * Sent through whichever channel is available on the page:
 *  - `gtag()` when GA4 is installed with the global site tag
 *  - `dataLayer` when it's installed through Google Tag Manager
 *  - `Shopify.analytics.publish()` so a Customer Events custom pixel can forward it
 *    (needed when GA comes from the Google & YouTube app, which runs sandboxed)
 */
const WHATSAPP_CLICK_EVENT = 'whatsapp_click';
const WHATSAPP_CLICK_LOCATIONS = {
  '.header-actions__whatsapp': 'header',
  '.whatsapp-sticky-button__link': 'sticky_button',
};

if (!window.whatsappClickTrackingBound) {
  window.whatsappClickTrackingBound = true;

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      for (const [selector, clickLocation] of Object.entries(WHATSAPP_CLICK_LOCATIONS)) {
        const link = event.target.closest(selector);
        if (!link) continue;

        trackWhatsappClick({
          click_location: clickLocation,
          link_url: link.getAttribute('href') ?? '',
          page_path: window.location.pathname,
        });
        return;
      }
    },
    { capture: true }
  );
}

/**
 * @param {{ click_location: string, link_url: string, page_path: string }} params
 */
function trackWhatsappClick(params) {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', WHATSAPP_CLICK_EVENT, params);
    } else if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: WHATSAPP_CLICK_EVENT, ...params });
    }

    window.Shopify?.analytics?.publish?.(WHATSAPP_CLICK_EVENT, params);
  } catch (error) {
    console.warn('WhatsApp click tracking failed', error);
  }
}
